/**
 * 井道 / 轿厢 Canvas 2D 渲染器（设计文档 render/shaft-renderer.ts，决策 D3）。
 *
 * 纯绘制：只消费快照状态，不回写内核。由 SimSession 的主线程 RAF 循环
 * 每帧用节流快照 + 运动学插值驱动（M1 决策：主线程驱动，Worker 延后到按需）。
 */

export interface RenderCar {
  id: string;
  /** 逻辑楼层，可为小数（运行段插值），floor=0 在底部。 */
  floor: number;
  direction: 1 | -1 | 0;
  load: number;
  capacity: number;
  doorOpen: boolean;
}

export interface ShaftLayout {
  width: number;
  height: number;
  floors: number;
}

export interface RenderState {
  cars: RenderCar[];
}

// 配色（深色主题，定义于 theme.ts）
import { THEME } from './theme';
const COL = THEME;

export function renderShafts(
  ctx: CanvasRenderingContext2D,
  state: RenderState,
  layout: ShaftLayout,
): void {
  const { width, height, floors } = layout;
  ctx.fillStyle = COL.bg;
  ctx.fillRect(0, 0, width, height);

  const n = Math.max(1, state.cars.length);
  // 近似方形网格排布，适配任意电梯数（大规模场景的概览雏形，D7）
  const cols = Math.max(1, Math.round(Math.sqrt(n * (width / height))));
  const rows = Math.ceil(n / cols);
  const cellW = width / cols;
  const cellH = height / rows;

  for (let i = 0; i < n; i++) {
    const car = state.cars[i]!;
    const cx = (i % cols) * cellW;
    const cy = Math.floor(i / cols) * cellH;
    drawShaft(ctx, cx, cy, cellW, cellH, floors, car);
  }
}

function drawShaft(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  floors: number,
  car: RenderCar,
): void {
  const pad = 6;
  const sx = x + pad;
  const sy = y + pad;
  const sw = w - pad * 2;
  const sh = h - pad * 2;

  // 井道背景
  ctx.fillStyle = COL.shaft;
  ctx.fillRect(sx, sy, sw, sh);

  // 楼层网格线
  ctx.strokeStyle = COL.grid;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let f = 0; f <= floors; f++) {
    const fy = sy + sh * (1 - f / floors);
    ctx.moveTo(sx, fy);
    ctx.lineTo(sx + sw, fy);
  }
  ctx.stroke();

  // 轿厢位置：floor=0 在底部，轿厢中心对齐楼层刻度
  const floorH = sh / floors;
  const carH = floorH * 0.82;
  const cy = sy + sh * (1 - (car.floor + 0.5) / floors) - carH / 2;
  const cx = sx + sw * 0.2;
  const cw = sw * 0.6;

  const ratio = car.capacity > 0 ? Math.min(1, car.load / car.capacity) : 0;
  ctx.fillStyle = ratio > 0.85 ? COL.carFull : COL.car;
  ctx.fillRect(cx, cy, cw, carH);

  // 载客数
  ctx.fillStyle = COL.text;
  ctx.font = `${Math.max(8, Math.floor(carH * 0.4))}px ui-monospace, monospace`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(`${car.load}`, cx + cw / 2, cy + carH / 2);

  // 方向箭头
  if (car.direction !== 0) {
    const ax = cx + cw + 6;
    const ay = cy + carH / 2;
    ctx.fillStyle = car.direction > 0 ? COL.up : COL.down;
    ctx.beginPath();
    if (car.direction > 0) {
      ctx.moveTo(ax, ay - 5);
      ctx.lineTo(ax + 8, ay + 5);
      ctx.lineTo(ax - 4, ay + 5);
    } else {
      ctx.moveTo(ax, ay + 5);
      ctx.lineTo(ax + 8, ay - 5);
      ctx.lineTo(ax - 4, ay - 5);
    }
    ctx.closePath();
    ctx.fill();
  }

  // 开门指示
  if (car.doorOpen) {
    ctx.strokeStyle = COL.door;
    ctx.lineWidth = 2;
    ctx.strokeRect(cx - 1, cy - 1, cw + 2, carH + 2);
  }
}
